import type { CurrentUser } from "@/lib/auth";
import {
  userIsAbrechnung,
  userIsBuero,
} from "@/lib/auth";
import type { Order } from "@/lib/projects";
import type { TimesheetSummary } from "@/lib/timesheets";
import { userIsMontage } from "@/projectOverview/permissions";
import {
  PROJECT_ACTION_COL_WIDTH,
  PROJECT_COL_MAX_WIDTH,
  PROJECT_COL_MIN_WIDTH,
  getColumnDefaultWidth,
  getColumnLabel,
  heroCustomFieldKey,
  isProjectActionColumn,
  renderCellValue,
  type HeroCustomField,
  type TimesheetBadge,
} from "@/pages/projekte/columns";

/** Pflichtspalte der Stundennachweis-Tabelle. */
export const TIMESHEET_REQUIRED_COLUMNS = ["number"] as const;

/** Stundennachweis-spezifische Spalten (nicht in der Projekt-Tabelle). */
export const TIMESHEET_NATIVE_COLUMNS = [
  "number",
  "project",
  "contact",
  "period",
  "hours",
  "status",
  "at_contact",
  "invoice_number",
  "invoice_value",
  "actions",
] as const;

export type TimesheetNativeColumnKey = (typeof TIMESHEET_NATIVE_COLUMNS)[number];

/** Projekt-Spalten, die auch in der Stundennachweis-Tabelle wählbar sind. */
export const TIMESHEET_PROJECT_DATA_COLUMNS = [
  "auftragsnummer",
  "kunde",
  "ansprechpartner",
  "ort",
  "ort_zusatz",
  "beschreibung",
  "hero_step",
  "project_status",
  "hours_usage",
  "timesheets",
] as const;

/** Aktionsspalten (sticky rechts). */
export const TIMESHEET_ACTION_COLUMNS = [
  "actions",
  "hero",
  "cover_print_default",
  "open",
] as const;

export type TimesheetColumnKey = string;

export const TIMESHEET_HIDDEN_BY_DEFAULT_COLUMNS: string[] = [
  "cover_print_default",
  "auftragsnummer",
  "kunde",
  "ansprechpartner",
  "ort",
  "ort_zusatz",
  "beschreibung",
  "hero_step",
  "project_status",
  "hours_usage",
  "timesheets",
  "hero",
  "open",
];

/** Breite pro Aktions-Button (wie Projekte-Tabelle). */
export const TIMESHEET_ACTION_BTN_WIDTH = 44;

/** Gesamtbreite der kombinierten Aktionsspalte abhängig von sichtbaren Buttons. */
export function getTimesheetActionColWidth(showElectronFolder: boolean): number {
  const buttonCount = 2 + (showElectronFolder ? 1 : 0);
  return buttonCount * TIMESHEET_ACTION_BTN_WIDTH;
}

export const TIMESHEET_COL_MIN_WIDTH = PROJECT_COL_MIN_WIDTH;
export const TIMESHEET_COL_MAX_WIDTH = PROJECT_COL_MAX_WIDTH;

const TIMESHEET_NATIVE_DEFAULT_WIDTHS: Partial<Record<TimesheetNativeColumnKey, number>> = {
  number: 128,
  project: 200,
  contact: 140,
  period: 130,
  hours: 88,
  status: 108,
  at_contact: 168,
  invoice_number: 120,
  invoice_value: 112,
  actions: getTimesheetActionColWidth(false),
};

export function getTimesheetColumnDefaultWidth(key: string, showElectronFolder = false): number {
  if (key === "actions") return getTimesheetActionColWidth(showElectronFolder);
  if (isProjectActionColumn(key)) return PROJECT_ACTION_COL_WIDTH;
  if ((TIMESHEET_PROJECT_DATA_COLUMNS as readonly string[]).includes(key)) {
    if (key === "project_status") return getColumnDefaultWidth("status");
    return getColumnDefaultWidth(key);
  }
  return TIMESHEET_NATIVE_DEFAULT_WIDTHS[key as TimesheetNativeColumnKey] ?? 120;
}

export function clampTimesheetColumnWidth(width: number): number {
  return Math.min(
    TIMESHEET_COL_MAX_WIDTH,
    Math.max(TIMESHEET_COL_MIN_WIDTH, Math.round(width)),
  );
}

export function getTimesheetColumnLabel(key: string, heroFields: HeroCustomField[] = []): string {
  if (key === "project_status") return "Projektstatus";
  if ((TIMESHEET_NATIVE_COLUMNS as readonly string[]).includes(key)) {
    switch (key as TimesheetNativeColumnKey) {
      case "number":
        return "Nr.";
      case "project":
        return "Projekt";
      case "contact":
        return "Ansprechpartner";
      case "period":
        return "Zeitraum";
      case "hours":
        return "Stunden";
      case "status":
        return "NW-Status";
      case "at_contact":
        return "NW bei AP";
      case "invoice_number":
        return "Rechnungsnr.";
      case "invoice_value":
        return "Rechnungsbetrag";
      case "actions":
        return "Aktion";
      default:
        return key;
    }
  }
  if (
    (TIMESHEET_PROJECT_DATA_COLUMNS as readonly string[]).includes(key) ||
    isProjectActionColumn(key) ||
    key.startsWith("cf_")
  ) {
    if (key === "project_status") return "Projektstatus";
    return getColumnLabel(key === "project_status" ? "status" : key, heroFields);
  }
  return key;
}

export function isTimesheetActionColumn(key: string): boolean {
  return (TIMESHEET_ACTION_COLUMNS as readonly string[]).includes(key);
}

export interface TimesheetColumnOptions {
  user: CurrentUser | null;
  heroFields: HeroCustomField[];
  showOpenFolderColumn: boolean;
}

function userGroups(user: CurrentUser | null): string[] {
  return (user?.groups as string[] | undefined) ?? [];
}

/** Alle Spalten, die der Nutzer aufgrund seiner Gruppe wählen darf. */
export function getAvailableTimesheetColumns(options: TimesheetColumnOptions): string[] {
  const { user, heroFields, showOpenFolderColumn } = options;
  const isBuero = userIsBuero(user);
  const isAbrechnung = userIsAbrechnung(user);

  const native = TIMESHEET_NATIVE_COLUMNS.filter((col) => {
    if (col === "invoice_number" || col === "invoice_value") {
      return isBuero || isAbrechnung;
    }
    if (col === "actions") return isBuero;
    return true;
  });

  const heroCf = heroFields.map(heroCustomFieldKey);
  const projectActions: string[] = ["hero"];
  if (userCanPickCoverPrintColumn(user)) projectActions.push("cover_print_default");
  if (showOpenFolderColumn) projectActions.push("open");

  return [
    ...native,
    ...TIMESHEET_PROJECT_DATA_COLUMNS,
    ...heroCf,
    ...projectActions,
  ];
}

export function getDefaultTimesheetColumns(options: TimesheetColumnOptions): string[] {
  return getAvailableTimesheetColumns(options).filter(
    (col) => !TIMESHEET_HIDDEN_BY_DEFAULT_COLUMNS.includes(col),
  );
}

export function normalizeTimesheetColumnSelection(
  selection: string[] | undefined | null,
  available: string[],
): string[] {
  const out: string[] = [];
  if (Array.isArray(selection)) {
    for (const col of selection) {
      if (typeof col !== "string") continue;
      const trimmed = col.trim();
      if (!trimmed || !available.includes(trimmed)) continue;
      if (!out.includes(trimmed)) out.push(trimmed);
    }
  }

  for (const required of TIMESHEET_REQUIRED_COLUMNS) {
    if (available.includes(required) && !out.includes(required)) out.unshift(required);
  }

  if (available.includes("ort_zusatz") && !out.includes("ort_zusatz")) {
    const ortIdx = out.indexOf("ort");
    if (ortIdx >= 0) out.splice(ortIdx + 1, 0, "ort_zusatz");
    else out.push("ort_zusatz");
  }

  const withoutActions = out.filter((c) => !isTimesheetActionColumn(c));
  const orderedActions = TIMESHEET_ACTION_COLUMNS.filter((c) => out.includes(c));
  return [...withoutActions, ...orderedActions];
}

export function resolveTimesheetColumns(
  settingsColumns: string[] | undefined | null,
  options: TimesheetColumnOptions,
): string[] {
  const available = getAvailableTimesheetColumns(options);
  const defaults = getDefaultTimesheetColumns(options);
  const source = settingsColumns?.length ? settingsColumns : defaults;
  return normalizeTimesheetColumnSelection(source, available);
}

/** @deprecated Verwende resolveTimesheetColumns mit gespeicherten Settings. */
export function getVisibleTimesheetColumns(user: CurrentUser | null): TimesheetNativeColumnKey[] {
  const options: TimesheetColumnOptions = {
    user,
    heroFields: [],
    showOpenFolderColumn: userIsBuero(user),
  };
  return resolveTimesheetColumns(null, options) as TimesheetNativeColumnKey[];
}

export function timesheetHasFolder(ts: TimesheetSummary): boolean {
  return Boolean(
    ts.ordnerpfad ||
      ts.pfad ||
      ts.sharepoint_path ||
      ts.sharepoint_folder_id ||
      ts.has_folder,
  );
}

export function getTimesheetFolderPath(ts: TimesheetSummary): string {
  return String(ts.ordnerpfad || ts.pfad || ts.sharepoint_path || "").trim();
}

export function formatTimesheetProjectLabel(ts: TimesheetSummary): string {
  const proj =
    ts.auftrag_display ||
    (ts.project_name as string | undefined) ||
    (ts.title as string | undefined) ||
    "";
  const year =
    ts.jahr ?? ts.year ?? (ts.created_at ? new Date(ts.created_at).getFullYear() : null);
  const yearNum = year != null ? Number(year) : null;
  if (yearNum && !Number.isNaN(yearNum)) {
    return proj ? `${proj}-${yearNum}` : String(yearNum);
  }
  return proj;
}

export function resolveTimesheetHeroProjectId(ts: TimesheetSummary): number | null {
  const raw = ts.hero_project_id;
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function formatTimesheetPeriod(ts: TimesheetSummary): string {
  const fmt = (raw: string | null | undefined) => {
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("de-DE");
  };
  const start = fmt(ts.period_start);
  const end = fmt(ts.period_end);
  if (start && end) return start === end ? start : `${start} – ${end}`;
  return start || end || "";
}

/** Mappt eine Timesheet-Zeile auf ein Order-Objekt für Projekt-Spalten. */
export function timesheetRowAsOrder(row: TimesheetSummary): Order {
  const auftragId = Number(row.auftrag_id ?? 0);
  const contactName = String(
    row.ansprechpartner ||
      [row.ansprechpartner_vorname, row.ansprechpartner_nachname].filter(Boolean).join(" ") ||
      row.ansprechpartner_nachname ||
      "",
  ).trim();

  const timesheetBadge: TimesheetBadge = {
    label:
      String(row.display_number || row.timesheet_number || row.id || "").trim() || "—",
    status: String(row.status_display || row.status || "draft"),
  };

  return {
    id: Number.isFinite(auftragId) ? auftragId : 0,
    hero_project_id: resolveTimesheetHeroProjectId(row),
    auftragsnummer: String(row.auftragsnummer || "").trim(),
    ansprechpartner_id: 0,
    ansprechpartner: contactName,
    kunde: String(row.kunde || "").trim(),
    hero_kunde: (row.hero_kunde as string | null | undefined) ?? null,
    use_hero_customer_name_in_projects: Boolean(row.use_hero_customer_name_in_projects),
    ort: String(row.ort || "").trim(),
    ort_zusatz: (row.ort_zusatz as string | null | undefined) ?? null,
    beschreibung: (row.beschreibung as string | null | undefined) ?? null,
    ordnernummer: String(row.ordnernummer || "").trim(),
    ordnerpfad: (row.ordnerpfad as string | null | undefined) ?? null,
    sharepoint_path: (row.sharepoint_path as string | null | undefined) ?? null,
    sharepoint_folder_id: (row.sharepoint_folder_id as string | null | undefined) ?? null,
    has_folder: timesheetHasFolder(row),
    legacy_import: row.legacy_import === true,
    ort_id: 0,
    jahr: row.jahr ?? row.year ?? null,
    hero_step_name: (row.hero_step_name as string | null | undefined) ?? null,
    project_completed: row.project_completed === true,
    hero_custom_fields: Array.isArray(row.hero_custom_fields)
      ? (row.hero_custom_fields as Order["hero_custom_fields"])
      : null,
    timesheets: [timesheetBadge as never],
    timesheet_numbers: [
      {
        number: timesheetBadge.label,
        timesheet_number: Number(row.timesheet_number ?? row.id ?? 0),
        display_number: row.display_number ?? null,
        status: String(row.status || "draft"),
      },
    ],
  };
}

export function renderTimesheetProjectCellValue(
  row: TimesheetSummary,
  key: string,
  heroFields: HeroCustomField[],
) {
  const order = timesheetRowAsOrder(row);
  if (key === "project_status") {
    return renderCellValue(order, "status", heroFields);
  }
  return renderCellValue(order, key, heroFields);
}

export function userCanPickCoverPrintColumn(user: CurrentUser | null): boolean {
  return userIsBuero(user) || userIsMontage(user, userGroups(user));
}
